import { captureException, shouldReportToSentry } from 'shared/sentry';
import { HTTP_STATUS } from '../constants/common';

/**
 * Standard response handler for API calls
 * Provides consistent success and error response formatting
 */
export class ResponseHandler {
  /**
   * Handle successful API response
   * @param {Object} response - Axios response object
   * @param {string} successMessage - Custom success message
   * @returns {Object} Formatted success response
   */
  static handleSuccess(response, successMessage = null) {
    const { data, status } = response;

    return {
      success: true,
      status: status || HTTP_STATUS.OK,
      message: successMessage || data?.message || 'Operation completed successfully',
      data: data?.data || data,
      meta: {
        timestamp: new Date().toISOString(),
        statusCode: status,
      }
    };
  }

  /**
   * Handle API error response
   * @param {Object} error - Axios error object
   * @param {string} fallbackMessage - Fallback error message
   * @returns {Object} Formatted error response
   */
  static handleError(error, fallbackMessage = 'An error occurred') {
    const { response, request, message } = error;

    // Network error (no response received) — client connectivity; not actionable in Sentry.
    if (request && !response) {
      return {
        success: false,
        status: 0,
        message: 'Network error. Please check your connection.',
        data: null,
        error: {
          type: 'NETWORK_ERROR',
          message: 'Unable to connect to the server',
          timestamp: new Date().toISOString(),
        }
      };
    }

    // Server responded with error status
    if (response) {
      const { status, data } = response;

      // 5xx capture happens in the axios interceptor (captureHttpErrorIfNeeded) to avoid duplicate events.

      return {
        success: false,
        status: status,
        message: data?.message || getStatusErrorMessage(status) || fallbackMessage,
        exceptionName: data?.exceptionName || null,
        data: data?.data || null,
        error: {
          type: 'API_ERROR',
          statusCode: status,
          message: data?.message || getStatusErrorMessage(status),
          exceptionName: data?.exceptionName || null,
          details: data?.errors || data?.details || data?.fields || null,
          timestamp: new Date().toISOString(),
        }
      };
    }

    // Other errors
    if (shouldReportToSentry(error)) {
      captureException(error, {
        tags: {
          errorType: 'unknown_error',
          errorHandler: 'ResponseHandler',
        },
        extra: {
          errorMessage: message || fallbackMessage,
        },
      });
    }

    return {
      success: false,
      status: 500,
      message: message || fallbackMessage,
      data: null,
      error: {
        type: 'UNKNOWN_ERROR',
        message: message || fallbackMessage,
        timestamp: new Date().toISOString(),
      }
    };
  }

  /**
   * Handle validation errors
   * @param {Object} validationErrors - Validation error object
   * @returns {Object} Formatted validation error response
   */
  static handleValidationError(validationErrors) {
    return {
      success: false,
      status: HTTP_STATUS.BAD_REQUEST,
      message: 'Validation failed',
      data: null,
      error: {
        type: 'VALIDATION_ERROR',
        message: 'Please check your input and try again',
        details: validationErrors,
        timestamp: new Date().toISOString(),
      }
    };
  }

  /**
   * Client-side parameter validation (before an API call).
   * @param {string} message - Specific validation failure message
   * @returns {Object} Formatted validation error response
   */
  static handleClientValidationError(message) {
    return {
      success: false,
      status: HTTP_STATUS.BAD_REQUEST,
      message,
      data: null,
      error: {
        type: 'VALIDATION_ERROR',
        message,
        timestamp: new Date().toISOString(),
      },
    };
  }

  /**
   * Handle authentication errors
   * @param {string} message - Error message
   * @returns {Object} Formatted authentication error response
   */
  static handleAuthError(message = 'Authentication failed') {
    return {
      success: false,
      status: HTTP_STATUS.UNAUTHORIZED,
      message: message,
      data: null,
      error: {
        type: 'AUTH_ERROR',
        message: message,
        timestamp: new Date().toISOString(),
      }
    };
  }

  /**
   * Handle permission errors
   * @param {string} message - Error message
   * @returns {Object} Formatted permission error response
   */
  static handlePermissionError(message = 'Access denied') {
    return {
      success: false,
      status: HTTP_STATUS.FORBIDDEN,
      message: message,
      data: null,
      error: {
        type: 'PERMISSION_ERROR',
        message: message,
        timestamp: new Date().toISOString(),
      }
    };
  }
}

/**
 * Get user-friendly error message based on HTTP status code
 * @param {number} status - HTTP status code
 * @returns {string} Error message
 */
const getStatusErrorMessage = (status) => {
  const errorMessages = {
    [HTTP_STATUS.BAD_REQUEST]: 'Invalid request. Please check your input.',
    [HTTP_STATUS.UNAUTHORIZED]: 'Authentication required. Please log in.',
    [HTTP_STATUS.FORBIDDEN]: 'Access denied. You do not have permission to perform this action.',
    [HTTP_STATUS.NOT_FOUND]: 'The requested resource was not found.',
    [HTTP_STATUS.INTERNAL_SERVER_ERROR]: 'Internal server error. Please try again later.',
    502: 'Bad gateway. The server is temporarily unavailable.',
    503: 'Service unavailable. Please try again later.',
    504: 'Gateway timeout. The request took too long to process.',
  };

  return errorMessages[status] || 'An unexpected error occurred.';
};

/**
 * Utility function to check if response is successful
 * @param {Object} response - Response object
 * @returns {boolean} Success status
 */
export const isSuccessResponse = (response) => {
  return response && response.success === true;
};

/**
 * Utility function to check if response is an error
 * @param {Object} response - Response object
 * @returns {boolean} Error status
 */
export const isErrorResponse = (response) => {
  return response && response.success === false;
};

/**
 * Utility function to extract error message from response
 * @param {Object} response - Response object
 * @returns {string} Error message
 */
export const getErrorMessage = (response) => {
  if (isErrorResponse(response)) {
    return response.message || 'An error occurred';
  }
  return null;
};

/**
 * Utility function to extract data from response
 * @param {Object} response - Response object
 * @returns {*} Response data
 */
export const getResponseData = (response) => {
  if (isSuccessResponse(response)) {
    return response.data;
  }
  return null;
};

export default ResponseHandler;
